import { useMemo, useCallback } from "react"
import type { IntradayBatchMetadata } from "model/state"
import { useAppDispatch } from "hooks/redux"
import type { FilterScope } from "helpers/filter-scope"
import { coreSlice } from "../store/core-slice"
import { optimizationSlice } from "../store/optimization-slice"
import { modelSlice } from "../store/model-slice"
import { useDashboardId } from "../context/dashboard-id"

export function useCoreActions() {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()

  const coreActions = useMemo(() => ({
    setModelId: (newModelId: string) =>
      dispatch(modelSlice.actions.setModelId({ dashboardId, modelId: newModelId })),
    setInstrument: (instrument: unknown) =>
      dispatch(coreSlice.actions.setInstrument({ dashboardId, instrument })),
    setPeriod: (period: unknown) =>
      dispatch(coreSlice.actions.setPeriod({ dashboardId, period })),
    setBoundaryDates: (earliestDate: unknown, latestDate: unknown) =>
      dispatch(coreSlice.actions.setBoundaryDates({ dashboardId, earliestDate, latestDate })),
    setUseLatestDate: (useLatestDate: boolean) =>
      dispatch(coreSlice.actions.setUseLatestDate({ dashboardId, useLatestDate })),
    setPerformanceStartDate: (performanceStartDate: unknown) =>
      dispatch(coreSlice.actions.setPerformanceStartDate({ dashboardId, performanceStartDate })),
    setPerformanceEndDate: (performanceEndDate: unknown) =>
      dispatch(coreSlice.actions.setPerformanceEndDate({ dashboardId, performanceEndDate })),
    setBase: (base: unknown, mainInstrumentMetadata?: unknown) =>
      dispatch(coreSlice.actions.setBase({ dashboardId, base, mainInstrumentMetadata })),
    setBaseOverride: (baseOverride: unknown) =>
      dispatch(coreSlice.actions.setBaseOverride({ dashboardId, baseOverride })),
    setIsBaseOverridden: (isBaseOverridden: boolean) =>
      dispatch(coreSlice.actions.setIsBaseOverridden({ dashboardId, isBaseOverridden })),
    setSecondInstrument: (secondInstrument: unknown, secondInstrumentStatus: unknown, overrideMode: unknown = null) =>
      dispatch(coreSlice.actions.setSecondInstrument({ dashboardId, secondInstrument, secondInstrumentStatus, overrideMode })),
    setFilter: (filter: unknown) =>
      dispatch(coreSlice.actions.setFilter({ dashboardId, filter })),
    setNormalization: (normalization: unknown) =>
      dispatch(coreSlice.actions.setNormalization({ dashboardId, normalization })),
    setFilterScope: (filterScope: FilterScope) =>
      dispatch(coreSlice.actions.setFilterScope({ dashboardId, filterScope })),
    setComparisonInstrument: (instrument: unknown) =>
      dispatch(coreSlice.actions.setComparisonInstrument({ dashboardId, instrument })),
    setIsEndVsStartDateComparison: (isEndVsStartDateComparison: boolean) =>
      dispatch(coreSlice.actions.setIsEndVsStartDateComparison({ dashboardId, isEndVsStartDateComparison })),
    setSelectedMode: (selectedMode: unknown) =>
      dispatch(coreSlice.actions.setSelectedMode({ dashboardId, selectedMode })),
    setIsAiDrawerOpen: (isAiDrawerOpen: boolean) =>
      dispatch(coreSlice.actions.setIsAiDrawerOpen({ dashboardId, isAiDrawerOpen })),
  }), [dispatch, dashboardId])

  const setIsIntraday = useCallback(
    (isIntraday: boolean) =>
      dispatch(coreSlice.actions.setIsIntraday({ dashboardId, isIntraday })),
    [dispatch, dashboardId],
  )

  const setIsEndDateIntraday = useCallback(
    (isEndDateIntraday: boolean) =>
      dispatch(coreSlice.actions.setIsEndDateIntraday({ dashboardId, isEndDateIntraday })),
    [dispatch, dashboardId],
  )

  const setIntradayBatchMetadata = useCallback(
    (intradayBatchMetadata: IntradayBatchMetadata | null) =>
      dispatch(coreSlice.actions.setIntradayBatchMetadata({ dashboardId, intradayBatchMetadata })),
    [dispatch, dashboardId],
  )

  const setIntradayPricingAsOf = useCallback(
    (intradayPricingAsOf: string | null) =>
      dispatch(coreSlice.actions.setIntradayPricingAsOf({ dashboardId, intradayPricingAsOf })),
    [dispatch, dashboardId],
  )

  const setIsOptimizePanelExpanded = useCallback(
    (isOptimizePanelExpanded: boolean) =>
      dispatch(optimizationSlice.actions.setIsOptimizePanelExpanded({ dashboardId, isOptimizePanelExpanded })),
    [dispatch, dashboardId],
  )

  const setOptimizeConfiguration = useCallback(
    (optimizeConfiguration: unknown) =>
      dispatch(optimizationSlice.actions.setOptimizeConfiguration({ dashboardId, optimizeConfiguration })),
    [dispatch, dashboardId],
  )

  const setOptimizeConfigurationFromUser = useCallback(
    (optimizeConfiguration: unknown) =>
      dispatch(optimizationSlice.actions.setOptimizeConfigurationFromUser({ dashboardId, optimizeConfiguration })),
    [dispatch, dashboardId],
  )

  const setPreviewOptimizeConfiguration = useCallback(
    (previewOptimizeConfiguration: unknown) =>
      dispatch(optimizationSlice.actions.setPreviewOptimizeConfiguration({ dashboardId, previewOptimizeConfiguration })),
    [dispatch, dashboardId],
  )

  const setInstrumentsMetadata = useCallback(
    (instrumentsMetadata: unknown) =>
      dispatch(coreSlice.actions.setInstrumentsMetadata({ dashboardId, instrumentsMetadata })),
    [dispatch, dashboardId],
  )

  const setOptimizationSourceKind = useCallback(
    (optimizationSourceKind: string) =>
      dispatch(optimizationSlice.actions.setOptimizationSourceKind({ dashboardId, optimizationSourceKind })),
    [dispatch, dashboardId],
  )

  const setOptimizationExperimentSource = useCallback(
    (optimizationExperimentSource: unknown) =>
      dispatch(optimizationSlice.actions.setOptimizationExperimentSource({ dashboardId, optimizationExperimentSource })),
    [dispatch, dashboardId],
  )

  const setLastAppliedOptimizationSourceKey = useCallback(
    (lastAppliedOptimizationSourceKey: unknown) =>
      dispatch(optimizationSlice.actions.setLastAppliedOptimizationSourceKey({ dashboardId, lastAppliedOptimizationSourceKey })),
    [dispatch, dashboardId],
  )

  return useMemo(() => ({
    ...coreActions,
    setIsIntraday,
    setIsEndDateIntraday,
    setIntradayBatchMetadata,
    setIntradayPricingAsOf,
    setIsOptimizePanelExpanded,
    setOptimizeConfiguration,
    setOptimizeConfigurationFromUser,
    setPreviewOptimizeConfiguration,
    setInstrumentsMetadata,
    setOptimizationSourceKind,
    setOptimizationExperimentSource,
    setLastAppliedOptimizationSourceKey,
  }), [
    coreActions,
    setIsIntraday,
    setIsEndDateIntraday,
    setIntradayBatchMetadata,
    setIntradayPricingAsOf,
    setIsOptimizePanelExpanded,
    setOptimizeConfiguration,
    setOptimizeConfigurationFromUser,
    setPreviewOptimizeConfiguration,
    setInstrumentsMetadata,
    setOptimizationSourceKind,
    setOptimizationExperimentSource,
    setLastAppliedOptimizationSourceKey,
  ])
}
